import { useEffect, useMemo, useState } from 'react';
import { Download, FileDown, Info } from 'lucide-react';
import {
  CARD_EXPORT_DEFAULT,
  CARD_EXPORT_FIELDS,
  CARD_SHEET_DEFAULT,
  CARD_SHEET_TEXT,
  EXPORT_SCOPE_TEXT,
  isFilterActive,
  REPORT_LAYOUT_TEXT,
  REPORT_LIMITS,
  type BoardFilter,
  type BoardSnapshot,
  type CardSheetOptions,
  type ExportScope,
  type ReportLayout,
} from '@kaba/shared';
import { ReportStatus, TemplateBar, usePdfAvailable, useReportJob, useReportTemplates } from '../reports/ReportJob';
import { useMe } from '../../lib/auth';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../lib/toast';
import { Button, Sheet } from '../ui/ui';

const FIELDS_KEY = 'kaba.export.fields';
const PDF_FIELDS_KEY = 'kaba.export.pdfFields';
/** PDF-Tabelle: wenige, gut lesbare Spalten (Hochformat höchstens 7) */
const PDF_DEFAULT = ['title', 'column', 'status', 'assignees', 'priority', 'due'];

function storedFields(key: string, def: string[]): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? 'null');
    return Array.isArray(v) && v.length ? v : [...def];
  } catch {
    return [...def];
  }
}

/**
 * Exportieren … (E01, Z01): Umfang eindeutig wählen, Anzahl vorab vom Server, Felder wählen,
 * dann Download – auch am Handy. Dieselbe Trefferliste wie die Zählung.
 */
export function ExportDialog({
  snapshot,
  filter,
  selectedIds,
  open,
  onClose,
}: {
  snapshot: BoardSnapshot;
  filter: BoardFilter;
  selectedIds?: string[];
  open: boolean;
  onClose: () => void;
}) {
  // Filter aus einer Berichtsvorlage überschreibt den aktuellen Filter der Oberfläche
  const [tplFilter, setTplFilter] = useState<BoardFilter | null>(null);
  const activeFilter = tplFilter ?? filter;
  const filtered = isFilterActive(activeFilter);
  const scopes: ExportScope[] = [...(selectedIds?.length ? (['selected'] as const) : []), ...(filtered ? (['filtered'] as const) : []), 'all'];
  const [scope, setScope] = useState<ExportScope>(scopes[0]!);
  const [archived, setArchived] = useState(false);
  const [format, setFormat] = useState<'csv' | 'pdf'>('csv');
  const [csvFields, setCsvFields] = useState<string[]>(() => storedFields(FIELDS_KEY, CARD_EXPORT_DEFAULT));
  const [pdfFields, setPdfFields] = useState<string[]>(() => storedFields(PDF_FIELDS_KEY, PDF_DEFAULT));
  const fields = format === 'pdf' ? pdfFields : csvFields;
  const setFields = format === 'pdf' ? setPdfFields : setCsvFields;
  const [count, setCount] = useState<{ n: number; max: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const pdfOk = usePdfAvailable();
  const [layout, setLayout] = useState<ReportLayout>('table');
  const [landscape, setLandscape] = useState(false);
  const [grouped, setGrouped] = useState(true);
  const [summary, setSummary] = useState(true);
  const [branding, setBranding] = useState(true);
  const [kanban, setKanban] = useState(false);
  const [compact, setCompact] = useState(false);
  const { data: meX } = useMe();
  const tpl = useReportTemplates('board', snapshot.board.id, open && !meX?.isGuest);
  const [sheet, setSheet] = useState<CardSheetOptions>(CARD_SHEET_DEFAULT);
  const report = useReportJob();
  useEffect(() => {
    if (open) report.reset();
    if (open) setTplFilter(null);
    if (open) setScope(scopes[0]!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const body = useMemo(
    () => ({ scope, filter: scope === 'filtered' ? activeFilter : undefined, cardIds: scope === 'selected' ? selectedIds : undefined, includeArchived: archived, fields }),
    [scope, activeFilter, selectedIds, archived, fields],
  );
  // Umfang nach jeder Änderung neu berechnen (E01)
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setCount(null);
    api
      .post<{ count: number; max: number }>(`/api/boards/${snapshot.board.id}/export/count`, { ...body, fields: ['id'] })
      .then((r) => alive && setCount({ n: r.count, max: r.max }))
      .catch(() => alive && setCount(null));
    return () => {
      alive = false;
    };
  }, [open, body, snapshot.board.id]);

  const allFields: [string, string][] = [...CARD_EXPORT_FIELDS.map(([k, l]) => [k, l] as [string, string]), ...snapshot.fields.map((f) => [`f:${f.id}`, f.name] as [string, string])];
  const toggle = (k: string, on: boolean) => {
    const next = on ? allFields.map(([x]) => x).filter((x) => x === k || fields.includes(x)) : fields.filter((x) => x !== k);
    setFields(next);
    try {
      localStorage.setItem(format === 'pdf' ? PDF_FIELDS_KEY : FIELDS_KEY, JSON.stringify(next));
    } catch {
      /* egal */
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/boards/${snapshot.board.id}/export/cards.csv`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = (await res.json().catch(() => ({}))) as { error?: string };
        throw new ApiError(res.status, d.error ?? `Fehler ${res.status}`);
      }
      const blob = await res.blob();
      const name = decodeURIComponent(/filename\*=UTF-8''([^;]+)/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'Karten.csv');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.info(count?.n === 1 ? '1 Karte exportiert.' : `${count?.n ?? ''} Karten exportiert.`.trim());
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const max = format === 'pdf' ? REPORT_LIMITS.cards : (count?.max ?? 5000);
  const tooMany = !!count && count.n > max;
  const tableMax = landscape ? REPORT_LIMITS.tableFieldsLandscape : REPORT_LIMITS.tableFieldsPortrait;
  const tableFields = fields.filter((f) => f !== 'board').length;
  const tooWide = format === 'pdf' && layout === 'table' && tableFields > tableMax;
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Karten exportieren" side="center">
      <div className="flex flex-col gap-4 p-4">
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-sm font-semibold">Umfang</legend>
          {scopes.map((s) => (
            <label key={s} className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
              <input type="radio" name="export-scope" className="size-5 accent-[var(--color-accent)]" checked={scope === s} onChange={() => setScope(s)} />
              {s === 'filtered' && tplFilter ? 'Filter der Vorlage' : EXPORT_SCOPE_TEXT[s]}
              {s === 'selected' && selectedIds ? ` (${selectedIds.length})` : ''}
            </label>
          ))}
          {scope === 'all' && filtered && <p className="text-xs text-[var(--ink-soft)]">Deine aktuellen Filter werden dabei nicht angewendet.</p>}
          <label className="mt-1 flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
            <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
            Archivierte Karten einschließen
          </label>
        </fieldset>

        <p className="rounded-lg bg-[var(--column)] p-3 text-sm" role="status" aria-live="polite">
          {count === null ? 'Zähle …' : tooMany ? `${count.n} Karten – zu viele für einen ${format === 'pdf' ? 'PDF-Bericht' : 'Export'} (höchstens ${max}). Bitte filtere enger.` : `${count.n} ${count.n === 1 ? 'Karte wird' : 'Karten werden'} exportiert.`}
        </p>

        {pdfOk && (
          <fieldset className="flex flex-col gap-1">
            <legend className="mb-1 text-sm font-semibold">Format</legend>
            <div className="flex flex-wrap gap-x-5">
              {(
                [
                  ['csv', 'Tabelle (CSV)'],
                  ['pdf', 'Bericht (PDF)'],
                ] as const
              ).map(([k, l]) => (
                <label key={k} className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
                  <input type="radio" name="export-format" className="size-5 accent-[var(--color-accent)]" checked={format === k} onChange={() => setFormat(k)} />
                  {l}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {format === 'pdf' && (
          <fieldset className="flex flex-col gap-1">
            <legend className="mb-1 text-sm font-semibold">Darstellung</legend>
            {(Object.keys(REPORT_LAYOUT_TEXT) as ReportLayout[]).map((k) => (
              <label key={k} className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
                <input type="radio" name="report-layout" className="size-5 accent-[var(--color-accent)]" checked={layout === k} onChange={() => setLayout(k)} />
                {REPORT_LAYOUT_TEXT[k]}
              </label>
            ))}
            {layout === 'table' && (
              <label className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
                <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={landscape} onChange={(e) => setLandscape(e.target.checked)} />
                Querformat (mehr Spalten)
              </label>
            )}
            <label className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
              <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} />
              Nach Spalten gruppieren
            </label>
            <label className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
              <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={summary} onChange={(e) => setSummary(e.target.checked)} />
              Zusammenfassung mit Zahlen
            </label>
            <label className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
              <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={kanban} onChange={(e) => setKanban(e.target.checked)} />
              Zusätzlich Kanban-Seiten (Spalten nebeneinander)
            </label>
            <label className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
              <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={compact} onChange={(e) => setCompact(e.target.checked)} />
              Kompakt (weniger Abstände, gleiche Schriftgröße)
            </label>
            {layout === 'sheets' &&
              (Object.keys(CARD_SHEET_TEXT) as (keyof CardSheetOptions)[]).map((k) => (
                <label key={k} className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
                  <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={sheet[k]} onChange={(e) => setSheet({ ...sheet, [k]: e.target.checked })} />
                  {CARD_SHEET_TEXT[k]}
                </label>
              ))}
          </fieldset>
        )}
        {(format === 'csv' || layout === 'table') && (
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-sm font-semibold">{format === 'pdf' ? 'Spalten der Tabelle' : 'Spalten in der Datei'}</legend>
          {tooWide && (
            <p className="text-sm text-amber-800 dark:text-amber-300" role="alert">
              {tableFields} Spalten sind zu viele für eine lesbare Tabelle – höchstens {tableMax}
              {landscape ? '' : ' im Hochformat'}. Wähle weniger Spalten{landscape ? '' : ', Querformat'} oder Kartenblätter.
            </p>
          )}
          <div className="grid grid-cols-1 gap-x-4 min-[420px]:grid-cols-2">
            {allFields.map(([k, l]) => (
              <label key={k} className="flex min-h-10 items-center gap-2 text-sm sm:min-h-8">
                <input type="checkbox" className="size-4 accent-[var(--color-accent)]" checked={fields.includes(k)} onChange={(e) => toggle(k, e.target.checked)} />
                {l}
              </label>
            ))}
          </div>
        </fieldset>
        )}

        <p className="flex gap-2 text-xs text-[var(--ink-soft)]">
          <Info size={14} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            {format === 'pdf'
              ? 'PDF mit Seitenzahlen, Datenstand und Zeitzone; Tabellenköpfe wiederholen sich auf jeder Seite.'
              : 'CSV für Excel und andere Tabellenprogramme (Semikolon, UTF-8).'}{' '}
            „Erledigt“ heißt: Haken gesetzt. Die Datei ist eigenständig – ein späterer Entzug des Zugangs holt sie nicht zurück.
          </span>
        </p>
        {format === 'pdf' && (
          <label className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
            <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={branding} onChange={(e) => setBranding(e.target.checked)} />
            Logo und Farbe des Boards verwenden
          </label>
        )}
        {!meX?.isGuest && (
          <TemplateBar
            t={tpl}
            current={() => ({
              format,
              scope: scope === 'filtered' ? 'filtered' : 'all',
              filter: scope === 'filtered' ? activeFilter : undefined,
              includeArchived: archived,
              fields,
              layout,
              landscape,
              groupBy: grouped ? 'column' : 'none',
              summary,
              options: sheet,
              branding,
              kanban,
              compact,
            })}
            onApply={(c) => {
              const cfg = c as { format: 'csv' | 'pdf'; scope: 'filtered' | 'all'; filter?: BoardFilter; includeArchived: boolean; fields: string[]; layout: ReportLayout; landscape: boolean; groupBy: string; summary: boolean; options: CardSheetOptions; branding: boolean };
              setFormat(cfg.format === 'pdf' && pdfOk ? 'pdf' : 'csv');
              setTplFilter(cfg.scope === 'filtered' && cfg.filter ? cfg.filter : null);
              setScope(cfg.scope === 'filtered' && cfg.filter && isFilterActive(cfg.filter) ? 'filtered' : 'all');
              setArchived(cfg.includeArchived);
              if (cfg.format === 'pdf') setPdfFields(cfg.fields);
              else setCsvFields(cfg.fields);
              setLayout(cfg.layout);
              setLandscape(cfg.landscape);
              setGrouped(cfg.groupBy === 'column');
              setSummary(cfg.summary);
              setSheet(cfg.options);
              setBranding(cfg.branding);
              setKanban(!!(c as { kanban?: boolean }).kanban);
              setCompact(!!(c as { compact?: boolean }).compact);
            }}
          />
        )}
        {format === 'pdf' && <ReportStatus r={report} />}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{format === 'pdf' && report.job ? 'Schließen' : 'Abbrechen'}</Button>
          {format === 'csv' ? (
            <Button variant="primary" disabled={busy || !fields.length || !count || count.n === 0 || tooMany} onClick={() => void download()}>
              <Download size={16} aria-hidden /> CSV herunterladen
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={report.starting || report.active || (layout === 'table' && !fields.length) || !count || count.n === 0 || tooMany || tooWide}
              onClick={() =>
                void report.start({
                  kind: 'board',
                  boardId: snapshot.board.id,
                  ...body,
                  layout,
                  landscape: layout === 'table' && landscape,
                  groupBy: grouped ? 'column' : 'none',
                  summary,
                  branding,
                  kanban,
                  compact,
                  options: sheet,
                })
              }
            >
              <FileDown size={16} aria-hidden /> {report.job?.status === 'done' ? 'Neu erstellen' : 'PDF erstellen'}
            </Button>
          )}
        </div>
      </div>
    </Sheet>
  );
}
